/**
 * /function-plotter — график, который ОТВЕЧАЕТ, а не только рисует.
 *
 * Школьнику нужен не рисунок, а то, что на нём найдено: нули, вершины,
 * асимптоты, пересечения, промежутки. Поэтому всё найденное выписано числами
 * в рейке рядом с холстом, а каждое число — кнопка, которая показывает точку
 * на графике. Рисование — draw.js, разбор строки — expr.js, численное
 * исследование — study.js, числа в записи страны — format.js.
 *
 * ⚠️ Раскладка: рейка (выражения + ответы) и холст — ОДНА высота в экран, рейка
 * листается внутри себя. На узком контейнере холст сверху, выражения под ним.
 * Никакого sticky и никакой автопрокрутки (CLAUDE.md).
 *
 * ⚠️ Состояние лежит в адресе (#f=…&x=…&p=…). Контракт `#f=<выражение>` держит
 * ссылка «Открыть в построителе графиков» из /equation-solver — формат только
 * расширяется, старые ключи читаются как раньше.
 */

import { readRow, hasTrig, MAX_PARAMS, isParamName } from './expr.js';
import { evaluate } from '../math-core/parse.js';
import { roots, extrema, niceStep, table, snapZero } from '../math-core/analyze.js';
import {
    breaks, asymptotesAtInfinity, intersections, parity, period, domain,
    intervals, inflections, tangent, integral, deriv,
} from './study.js';
import { paint, viewMap } from './draw.js';
import { motionRole, quadOf, describe, meetings, linearLaw, unitsInNote } from './motion.js';
import * as FMT from './format.js';

const CFG = window.__FP_CFG || {};
const UI = CFG.i18n || {};
const SLUG = 'function-plotter';
const $ = (id) => document.getElementById(id);
const t = (k, d) => (UI[k] != null && UI[k] !== '' ? UI[k] : (d != null ? d : k));
const LOC = { decimal: UI.decimal === ',' ? ',' : '.', ptSep: UI.pt_sep || '; ' };
const COLORS = ['#2f7df6', '#e5484d', '#1f9d55', '#8e4ec6', '#e8890c', '#0e9aa7', '#d6409f', '#6b8e23'];
const MAX_ROWS = COLORS.length;
const HOME = { x0: -10, x1: 10, y0: -6, y1: 6 };
/* Закон движения живёт при t ≥ 0: левая половина обычного вида была бы пустой. */
const MOTION_HOME = { x0: -1, x1: 11, y0: -6, y1: 6 };
const fin = Number.isFinite;

/* Общее окружение вычислений: параметры лежат в нём, ось каждая функция
   ставит сама перед вызовом. Вычисление синхронное, поэтому один объект на
   всех безопасен. */
const ENV = Object.create(null);

const state = {
    rows: [],                 // {uid, src, color, hidden, t0, t1, read, b}
    params: Object.create(null),   // name → {v, min, max}
    paramOrder: [],
    view: { ...HOME },
    auto: true,
    pi: null,                 // null — подписи в π, если есть тригонометрия
    hover: null,
    selected: null,
    facts: { groups: [], inter: [], poi: [], asym: [] },
    studyUid: null,
    deriv: false,
    playing: new Set(),
    assigned: Object.create(null),   // параметр → число из строки «a = 2»
    viewTouched: false,       // человек двигал вид (или вид пришёл из ссылки)
    units: 'm-s',             // единицы законов движения
    unitsManual: false,       // выбраны человеком — пояснение их не меняет
};
let uidSeq = 0;

/* ── строки ──────────────────────────────────────────────────────────────── */
function addRow(src, extra) {
    const used = new Set(state.rows.map((r) => r.color));
    const color = COLORS.find((c) => !used.has(c)) || COLORS[state.rows.length % COLORS.length];
    const row = Object.assign({ uid: ++uidSeq, src: src || '', color, hidden: false, t0: '0', t1: '2π', read: null, b: null }, extra || {});
    state.rows.push(row);
    return row;
}

function readAll() {
    for (const row of state.rows) row.read = readRow(row.src);
    // «a = 2» рядом с «y = ax²» — значение параметра a, а не закон «ускорение
    // постоянно»: решает соседняя строка, где a стоит параметром.
    const used = new Set();
    for (const row of state.rows) {
        const r = row.read;
        if (r && r.kind !== 'error' && r.kind !== 'empty' && r.kind !== 'assign') for (const p of r.params || []) used.add(p);
    }
    for (const row of state.rows) {
        const r = row.read;
        if (r && r.kind === 'motion' && r.assign && used.has(r.assign.name)) {
            row.read = { kind: 'assign', op: '=', name: r.assign.name, ast: r.assign.ast, params: [], note: r.note };
        }
    }
    unitsFromNotes();
    syncParams();
    for (const row of state.rows) row.b = bind(row);
}

function syncParams() {
    // Число из строки «x0 = 5» главнее ползунка: ползунка у такого параметра нет.
    const fixed = Object.create(null);
    for (const row of state.rows) {
        const r = row.read;
        if (!r || r.kind !== 'assign' || row.hidden) continue;
        const v = evaluate(r.ast, ENV);
        if (fin(v)) fixed[r.name] = v;
    }
    state.assigned = fixed;
    const order = [];
    for (const row of state.rows) {
        const r = row.read;
        if (!r || r.kind === 'error' || r.kind === 'empty') continue;
        for (const p of r.params || []) if (order.indexOf(p) < 0 && !(p in fixed) && order.length < MAX_PARAMS) order.push(p);
    }
    for (const p in fixed) ENV[p] = fixed[p];
    for (const p of order) {
        if (!state.params[p]) state.params[p] = { v: 1, min: -10, max: 10 };
        ENV[p] = state.params[p].v;
    }
    for (const p of state.paramOrder) if (order.indexOf(p) < 0) state.playing.delete(p);
    const changed = order.join() !== state.paramOrder.join();
    state.paramOrder = order;
    if (changed) renderParams();
}

/** Константа из поля «t от/до», «x₀», пределов интеграла: «2π», «pi/2», «-1,5». */
function constOf(src) {
    const r = readRow(String(src || '').trim());
    if (r.kind !== 'explicit') return NaN;
    ENV.x = NaN;
    return evaluate(r.ast, ENV);
}

function bind(row) {
    const r = row.read;
    if (!r || r.kind === 'error' || r.kind === 'empty') return null;
    const b = { uid: row.uid, kind: r.kind, op: r.op, color: row.color, hidden: row.hidden, label: labelOf(row) };
    switch (r.kind) {
        case 'explicit':
        case 'ineq':
            b.f = (x) => { ENV.x = x; return evaluate(r.ast, ENV); };
            break;
        case 'motion':
            // Для всего, что ищет и рисует y(x), закон движения — та же функция,
            // только по горизонтали время. t < 0 — «до начала отсчёта», его нет.
            // ⚠️ t бывает и ползунком соседней строки (y = tx): его значение
            // возвращается на место после каждого вызова.
            b.kind = 'explicit';
            b.time = { head: r.head, index: r.index, role: motionRole(r.head) };
            b.f = (tt) => {
                if (!(tt >= 0)) return NaN;
                const keep = ENV.t;
                ENV.t = tt;
                const val = evaluate(r.ast, ENV);
                ENV.t = keep;
                return val;
            };
            break;
        case 'xofy':
        case 'xineq':
            b.fy = (y) => { ENV.y = y; return evaluate(r.ast, ENV); };
            break;
        case 'polar':
            b.fp = (tt) => { ENV.t = tt; ENV.theta = tt; const rr = evaluate(r.ast, ENV); return [rr * Math.cos(tt), rr * Math.sin(tt)]; };
            break;
        case 'param':
            b.fp = (tt) => { ENV.t = tt; ENV.theta = tt; return [evaluate(r.asts[0], ENV), evaluate(r.asts[1], ENV)]; };
            break;
        case 'implicit':
            b.F = (x, y) => { ENV.x = x; ENV.y = y; return evaluate(r.ast, ENV); };
            break;
        case 'points':
            b.ptsAst = r.pts;
            break;
        default:
            return null;
    }
    return b;
}

function labelOf(row) {
    const s = String(row.src || '').trim();
    const r = row.read;
    if (r && r.kind === 'explicit' && !/=/.test(s)) return 'y = ' + s;
    return s;
}

const explicitRows = () => state.rows.filter((r) => r.b && r.b.kind === 'explicit' && !r.hidden);

/* ── законы движения ─────────────────────────────────────────────────────── */
/** Все видимые функции — законы движения: ось — время, а не x. */
function allMotion() {
    const live = explicitRows();
    return live.length > 0 && live.every((r) => r.b.time);
}
const axisVar = () => (allMotion() ? 't' : 'x');
const SUB = '₀₁₂₃₄₅₆₇₈₉';
/** «x», «x₁», «vₓ» — как в тетради (подстрочного «y» в Юникоде нет — «vy»). */
function headText(time, zero) {
    const base = time.head.length > 1 ? time.head[0] + (time.head[1] === 'x' ? 'ₓ' : time.head[1]) : time.head;
    const idx = String(time.index || '').replace(/\d/g, (d) => SUB[Number(d)]);
    return base + (zero ? '₀' : '') + idx;
}
/*
 * Единицы закона движения. Числа от единиц не зависят — «x = 5 + 60t» с t в
 * часах и x в километрах считается так же; меняются только подписи. До 22.10
 * подпись была всегда «м, с», и задача «60 км/ч» отвечала «v₀ = 60 м/с».
 */
const UNITS = { 'm-s': ['m', 's'], 'km-h': ['km', 'h'], 'm-min': ['m', 'min'], 'cm-s': ['cm', 's'] };
const unitPair = () => UNITS[state.units] || UNITS['m-s'];
const unitT = () => t('unit_' + unitPair()[1]);
function unitOf(role) {
    const d = t('unit_' + unitPair()[0]), tt = unitT();
    return role === 'vel' ? d + '/' + tt : role === 'acc' ? d + '/' + tt + '²' : d;
}

function unitsFromNotes() {
    if (state.unitsManual) return;
    for (const row of state.rows) {
        const r = row.read;
        const u = r && r.note && r.kind === 'motion' ? unitsInNote(r.note) : null;
        if (u && u !== state.units) { state.units = u; syncUnitsUi(); ae('units', { u, how: 'note' }); return; }
    }
}

function syncUnitsUi() {
    const sel = $('fpUnits');
    if (sel && sel.value !== state.units) sel.value = state.units;
    const box = $('fpUnitsRow');
    if (box) box.hidden = !state.rows.some((r) => r.read && r.read.kind === 'motion');
}
/** Имя параметра как в тетради: «x0» → «x₀». */
const paramText = (n) => String(n).replace(/0$/, '₀');
const withUnit = (v, unit) => FMT.num(v, LOC) + ' ' + unit;
/** Число в поле ввода: знак страны, без хвоста двоичного шума. */
const inputNum = (v) => FMT.num(Math.round(v * 1e6) / 1e6, LOC);

/** Имена осей для холста: «t, с» и «x, м» (у разных величин — без единиц). */
function axisNames() {
    if (!allMotion()) return null;
    const heads = [];
    const roles = new Set();
    for (const r of explicitRows()) {
        roles.add(r.b.time.role);
        const h = headText({ head: r.b.time.head, index: '' });
        if (heads.indexOf(h) < 0) heads.push(h);
    }
    const y = roles.size === 1 && heads.length === 1 ? heads[0] + ', ' + unitOf([...roles][0]) : heads.join(', ');
    return { x: 't, ' + unitT(), y };
}

/* ── адрес ───────────────────────────────────────────────────────────────── */
const round = (v) => Math.round(v * 1e6) / 1e6;
function saveHash() {
    const live = state.rows.filter((r) => r.src.trim());
    const v = state.view;
    let hash = '#f=' + live.map((r) => encodeURIComponent(r.src)).join(';')
        + '&x=' + round(v.x0) + ',' + round(v.x1)
        + (state.auto ? '' : '&y=' + round(v.y0) + ',' + round(v.y1));
    if (state.paramOrder.length) {
        hash += '&p=' + state.paramOrder.map((n) => {
            const p = state.params[n];
            return n + ':' + round(p.v) + ':' + round(p.min) + ':' + round(p.max);
        }).join(',');
    }
    const off = live.map((r, i) => (r.hidden ? i : -1)).filter((i) => i >= 0);
    if (off.length) hash += '&off=' + off.join(',');
    const tr = live.map((r, i) => ((r.t0 !== '0' || r.t1 !== '2π') && r.read && (r.read.kind === 'polar' || r.read.kind === 'param')
        ? i + ':' + encodeURIComponent(r.t0) + ':' + encodeURIComponent(r.t1) : '')).filter(Boolean);
    if (tr.length) hash += '&t=' + tr.join(',');
    if (state.pi !== null) hash += '&pi=' + (state.pi ? 1 : 0);
    if (state.units !== 'm-s') hash += '&u=' + state.units;
    try { history.replaceState(null, '', hash); } catch (_) { /* песочница */ }
}

function loadHash() {
    const h = String(location.hash || '').replace(/^#/, '');
    if (!h) return false;
    const q = new URLSearchParams(h.replace(/\+/g, '%2B'));
    const list = FMT.hashRows(h, MAX_ROWS);
    if (!list.length) return false;
    state.rows = [];
    list.forEach((src) => addRow(src));
    const x = (q.get('x') || '').split(',').map(Number);
    state.viewTouched = false;
    if (x.length === 2 && x.every(fin) && x[1] > x[0]) {
        state.view.x0 = x[0]; state.view.x1 = x[1];
        // Вид из ссылки — выбор человека, если он не исходный.
        state.viewTouched = !((x[0] === HOME.x0 && x[1] === HOME.x1) || (x[0] === MOTION_HOME.x0 && x[1] === MOTION_HOME.x1));
    }
    const y = (q.get('y') || '').split(',').map(Number);
    state.auto = true;
    if (y.length === 2 && y.every(fin) && y[1] > y[0]) { state.view.y0 = y[0]; state.view.y1 = y[1]; state.auto = false; }
    for (const part of (q.get('p') || '').split(',')) {
        const [n, v, lo, hi] = part.split(':');
        if (!isParamName(n || '')) continue;
        const nums = [v, lo, hi].map(Number);
        if (!nums.every(fin)) continue;
        state.params[n] = { v: nums[0], min: Math.min(nums[1], nums[2]), max: Math.max(nums[1], nums[2]) };
    }
    for (const i of FMT.hiddenRows(q.get('off'), state.rows.length)) state.rows[i].hidden = true;
    for (const part of (q.get('t') || '').split(',')) {
        const [i, a, b] = part.split(':');
        const row = state.rows[Number(i)];
        if (row && a != null && b != null) { try { row.t0 = decodeURIComponent(a); row.t1 = decodeURIComponent(b); } catch (_) {} }
    }
    const pi = q.get('pi');
    state.pi = pi === '1' ? true : pi === '0' ? false : null;
    const u = q.get('u');
    state.units = u && UNITS[u] ? u : 'm-s';
    state.unitsManual = !!(u && UNITS[u]);
    return true;
}

/* ── вид ─────────────────────────────────────────────────────────────────── */
function canvasSize() {
    const c = $('fpCanvas');
    const r = c ? c.getBoundingClientRect() : { width: 600, height: 400 };
    return { w: Math.max(1, Math.round(r.width)), h: Math.max(1, Math.round(r.height)) };
}

/** Подбор вертикального окна по видимому куску функций y(x). */
function autoY() {
    const live = explicitRows();
    const { w, h } = canvasSize();
    const v = state.view;
    if (!live.length) {
        // Окружность без функций: единицы по осям обязаны быть равными, иначе
        // x² + y² = 9 выглядит эллипсом.
        const cy = (v.y0 + v.y1) / 2;
        const half = (v.x1 - v.x0) * h / w / 2;
        v.y0 = cy - half; v.y1 = cy + half;
        return;
    }
    const vals = [];
    for (const row of live) {
        for (let i = 0; i <= 240; i++) {
            const y = row.b.f(v.x0 + (v.x1 - v.x0) * (i / 240));
            if (fin(y) && Math.abs(y) < 1e7) vals.push(y);
        }
    }
    if (!vals.length) return;
    vals.sort((a, b) => a - b);
    // Хвосты отбрасываем: одна асимптота не должна сплющивать весь график.
    const lo = vals[Math.floor(vals.length * 0.02)];
    const hi = vals[Math.floor(vals.length * 0.98)];
    let pad = (hi - lo) * 0.15 || 1;
    if (hi - lo < 1e-9) pad = Math.max(1, Math.abs(hi) * 0.5);
    v.y0 = lo - pad;
    v.y1 = hi + pad;
}

function setAuto(on) {
    state.auto = on;
    const b = $('fpAutoY');
    if (b) b.setAttribute('aria-pressed', on ? 'true' : 'false');
}

function piOn() {
    if (state.pi !== null) return state.pi;
    // Время в секундах долями π не подписывают: x = 5sin(2t) — тоже про секунды.
    return state.rows.some((r) => r.b && !r.hidden && !r.b.time && (r.b.kind === 'explicit' || r.b.kind === 'ineq') && hasTrig(r.read.ast));
}

/* ── рисование ───────────────────────────────────────────────────────────── */
function themeColors(light) {
    if (light) return { ink: '#111827', muted: '#4b5563', grid: '#d5dae1', bg: '#ffffff', panel: '#ffffff' };
    const css = getComputedStyle(document.documentElement);
    const g = (n, d) => (css.getPropertyValue(n) || '').trim() || d;
    return {
        ink: g('--text-primary', '#e6eaf0'),
        muted: g('--text-muted', '#98a3b0'),
        grid: g('--border', '#2b333c'),
        bg: g('--bg-secondary', '#12161b'),
        panel: g('--bg-primary', '#0b0f14'),
    };
}

function sceneRows() {
    return state.rows.filter((r) => r.b).map((r) => {
        const b = Object.assign({}, r.b, { hidden: r.hidden });
        if (b.kind === 'points') b.pts = b.ptsAst.map((p) => ({ x: evaluate(p.x, ENV), y: evaluate(p.y, ENV) }));
        if (b.kind === 'polar' || b.kind === 'param') {
            const a = constOf(r.t0), z = constOf(r.t1);
            b.tRange = fin(a) && fin(z) && z !== a ? [a, z] : [0, 2 * Math.PI];
        }
        if (state.deriv && r.uid === state.studyUid && b.f) b.derivative = (x) => deriv(b.f, x);
        return b;
    });
}

function buildScene(w, h, extra) {
    return Object.assign({
        w, h,
        view: state.view,
        theme: themeColors(false),
        piAxis: piOn(),
        axisNames: axisNames(),
        rows: sceneRows(),
        poi: state.facts.poi,
        asymptotes: state.facts.asym,
        selected: state.selected,
        hover: state.hover,
        tangent: tangentScene(),
        integral: integralScene(),
    }, extra || {});
}

let rafId = 0;
function schedule() {
    if (rafId) return;
    rafId = requestAnimationFrame(() => { rafId = 0; draw(); });
}

function draw() {
    const cvs = $('fpCanvas');
    if (!cvs) return;
    const { w, h } = canvasSize();
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    const bw = Math.round(w * dpr), bh = Math.round(h * dpr);
    if (cvs.width !== bw || cvs.height !== bh) { cvs.width = bw; cvs.height = bh; }
    const ctx = cvs.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    paint(ctx, buildScene(w, h));
}

/* ── что найдено ─────────────────────────────────────────────────────────── */
function computeFacts() {
    const v = state.view;
    const span = v.x1 - v.x0;
    const spanY = v.y1 - v.y0;
    const groups = [];
    const poi = [];
    const asym = [];
    const live = explicitRows();
    for (const row of live) {
        const f = row.b.f;
        const g = { uid: row.uid, color: row.color, label: row.b.label, roots: [], ext: [], lines: [] };
        roots(f, v.x0, v.x1).slice(0, 12).forEach((x, i) => {
            const p = { id: row.uid + ':r' + i, x: snapZero(x, span), y: 0, color: row.color };
            g.roots.push(p); poi.push(p);
        });
        const y0 = f(0);
        if (fin(y0) && v.x0 <= 0 && v.x1 >= 0 && !g.roots.some((p) => p.x === 0)) {
            const p = { id: row.uid + ':y', x: 0, y: snapZero(y0, spanY), color: row.color };
            g.yint = p; poi.push(p);
        } else if (fin(y0)) {
            g.yint = { id: row.uid + ':y', x: 0, y: snapZero(y0, spanY), color: row.color, off: !(v.x0 <= 0 && v.x1 >= 0) };
            if (!g.yint.off) poi.push(g.yint);
        }
        const brk = breaks(f, v.x0, v.x1);
        if (row.b.time) {
            g.time = row.b.time;
            g.mo = describe(row.b.time.role, quadOf(f));
            if (g.mo.stop) {
                g.stop = { id: row.uid + ':s', x: g.mo.stop.t, y: g.mo.role === 'coord' ? g.mo.stop.x : 0, color: row.color };
                poi.push(g.stop);
            }
        }
        // Вершина параболы закона движения — та же остановка, что выписана выше.
        const extOk = !(g.mo && g.mo.type !== 'nonuniform');
        if (extOk) extrema(f, v.x0, v.x1).filter((e) => !brk.some((b) => Math.abs(b.x - e.x) < span / 400)).slice(0, 8).forEach((e, i) => {
            const p = { id: row.uid + ':e' + i, x: snapZero(e.x, span), y: snapZero(e.y, spanY), kind: e.kind, color: row.color };
            g.ext.push(p); poi.push(p);
        });
        g.brk = brk;
        for (const b of brk) if (b.kind === 'asymptote') asym.push({ kind: 'v', x: b.x, color: row.color });
        // «Асимптота y = 4x + 5 при x → +∞» у закона движения — сам закон в чужих
        // буквах: задача про время такого не спрашивает.
        g.inf = row.b.time ? [] : asymptotesAtInfinity(f).filter((a) => !isThatLine(f, a, v));
        for (const a of g.inf) asym.push({ kind: 'o', side: a.side, k: a.k, b: a.b, color: row.color });
        groups.push(g);
    }
    const inter = [];
    for (let i = 0; i < live.length; i++) {
        for (let j = i + 1; j < live.length; j++) {
            const A = live[i], B = live[j];
            const brkA = groups[i].brk, brkB = groups[j].brk;
            // Два тела с координатой одной оси: пересечение графиков — ВСТРЕЧА, и
            // её спрашивают, даже если она за краем экрана, — для равномерного и
            // равноускоренного движения она считается точно.
            const ta = A.b.time, tb = B.b.time;
            const meet = ta && tb && ta.role === 'coord' && tb.role === 'coord' && ta.head === tb.head;
            // x(t) и v(t), закон движения и y(x): величины разные, и точка, где их
            // графики пересеклись, ничего не значит — «пересечение (5; 25)» только путает.
            if ((ta || tb) && !meet) continue;
            const exact = meet ? meetings(quadOf(A.b.f), quadOf(B.b.f)) : undefined;
            if (meet && exact !== undefined && (exact === null || groups[i].mo.type !== 'nonuniform' && groups[j].mo.type !== 'nonuniform')) {
                const pts = [], far = [];
                (exact || []).forEach((m, k) => {
                    const p = { id: A.uid + ':m' + B.uid + ':' + k, x: m.t, y: m.x, color: A.color };
                    if (m.t >= v.x0 && m.t <= v.x1 && m.x >= v.y0 && m.x <= v.y1) pts.push(p); else far.push(p);
                });
                inter.push({ a: A, b: B, pts, far, meet: true, same: exact === null });
                poi.push(...pts);
                continue;
            }
            const pts = intersections(A.b.f, B.b.f, v.x0, v.x1)
                .filter((p) => ![...brkA, ...brkB].some((b) => Math.abs(b.x - p.x) < span / 400))
                .slice(0, 8)
                .map((p, k) => ({ id: A.uid + ':i' + B.uid + ':' + k, x: snapZero(p.x, span), y: snapZero(p.y, spanY), color: A.color }));
            if (pts.length) { inter.push({ a: A, b: B, pts, meet }); poi.push(...pts); }
        }
    }
    state.facts = { groups, inter, poi, asym };
    // Выбранная точка обязана оставаться той же точкой: после сдвига графика
    // её id мог достаться другой вершине.
    if (state.selected && state.selected.id !== 'curve') {
        const again = poi.find((p) => p.id === state.selected.id);
        state.selected = again ? { ...again, label: FMT.point(again.x, again.y, LOC) } : null;
    }
}

/** Функция и есть эта прямая: у y = kx + b «асимптоты y = kx + b» не бывает. */
function isThatLine(f, a, v) {
    for (let i = 0; i <= 6; i++) {
        const x = v.x0 + (v.x1 - v.x0) * i / 6;
        const y = f(x);
        if (fin(y) && Math.abs(y - (a.k * x + a.b)) > 1e-9 * Math.max(1, Math.abs(y))) return false;
    }
    return true;
}

function el(tag, cls, text) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
}

function renderFacts() {
    const box = $('fpFacts');
    if (!box) return;
    box.textContent = '';
    const { groups, inter } = state.facts;
    let any = false;
    const lineOf = (card, label, pts) => {
        if (!pts || !pts.length) return;
        const row = el('div', 'fp-fl');
        row.append(el('span', 'fp-fl-l', label));
        const vals = el('span', 'fp-fl-v');
        for (const p of pts) {
            const b = el('button', 'fp-pt', p.text || FMT.point(p.x, p.y, LOC));
            b.type = 'button';
            b.dataset.poi = p.id;
            b.style.setProperty('--c', p.color);
            if (state.selected && state.selected.id === p.id) b.setAttribute('aria-pressed', 'true');
            b.addEventListener('click', () => { selectPoi(p.id); ae('poi_click', { from: 'list', what: poiWhat(p.id) }); });
            vals.append(b);
        }
        row.append(vals);
        card.append(row);
    };
    const textOf = (card, label, text) => {
        const row = el('div', 'fp-fl');
        row.append(el('span', 'fp-fl-l', label), el('span', 'fp-fl-t', text));
        card.append(row);
    };
    for (const g of groups) {
        const card = el('div', 'fp-fact');
        card.style.setProperty('--c', g.color);
        const head = el('div', 'fp-fact-h');
        head.append(el('i', 'fp-sw'), el('code', null, g.label));
        card.append(head);
        if (g.mo) {
            for (const m of motionLines(g)) {
                if (m.pts) lineOf(card, m.label, m.pts); else textOf(card, m.label, m.text);
            }
            const add = companionRow(g);
            if (add) {
                const b = el('button', 'tc-btn sm ghost fp-mo-add', add.label);
                b.type = 'button';
                b.addEventListener('click', () => {
                    if (state.rows.length >= MAX_ROWS) return;
                    // Пустую последнюю строку занимаем, а не плодим ещё одну.
                    const last = state.rows[state.rows.length - 1];
                    if (last && !last.src.trim()) last.src = add.src; else addRow(add.src);
                    renderInputs();
                    refresh(false);
                    ae('motion_companion', { what: add.what });
                });
                card.append(b);
            }
        }
        const known = g.mo && g.mo.type !== 'nonuniform';
        // У закона движения «нули» — моменты, когда величина равна нулю, а точка
        // на оси Oy — начальный момент; при известных x₀, v₀ они уже выписаны.
        if (!(known && g.mo.role === 'vel' && g.stop)) lineOf(card, g.time ? headText(g.time) + ' = 0' : t('facts_roots'), g.roots);
        if (g.yint && !known) lineOf(card, g.time ? 't = 0' : t('facts_yint'), [g.yint]);
        lineOf(card, t('facts_min'), g.ext.filter((e) => e.kind === 'min'));
        lineOf(card, t('facts_max'), g.ext.filter((e) => e.kind === 'max'));
        const va = g.brk.filter((b) => b.kind === 'asymptote').map((b) => 'x = ' + FMT.num(b.x, LOC));
        if (va.length) textOf(card, t('facts_vasym'), va.join(', '));
        const jumps = g.brk.filter((b) => b.kind === 'jump').map((b) => 'x = ' + FMT.num(b.x, LOC));
        if (jumps.length) textOf(card, t('facts_jump'), jumps.join(', '));
        for (const a of g.inf) {
            const where = a.side > 0 ? t('facts_plus_inf') : a.side < 0 ? t('facts_minus_inf') : '';
            textOf(card, t('facts_asym'), FMT.line(a.k, a.b, LOC) + (where ? ' ' + where : ''));
        }
        if (card.childElementCount === 1) card.append(el('p', 'fp-fl-none', t('facts_none')));
        box.append(card);
        any = true;
    }
    for (const it of inter) {
        const card = el('div', 'fp-fact fp-fact-x');
        card.style.setProperty('--c', it.a.color);
        const head = el('div', 'fp-fact-h');
        const sw2 = el('i', 'fp-sw');
        sw2.style.setProperty('--c', it.b.color);
        head.append(el('i', 'fp-sw'), sw2, el('span', null, it.meet ? t('facts_meet') : t('facts_inter')));
        card.append(head);
        for (const m of meetLines(it)) {
            if (m.pts) lineOf(card, m.label, m.pts); else textOf(card, m.label, m.text);
        }
        box.append(card);
        any = true;
    }
    box.hidden = !any;
    const empty = $('fpFactsEmpty');
    if (empty) empty.hidden = any;
    const note = $('fpFactsNote');
    if (note) note.hidden = !any;
    const copy = $('fpCopyAns');
    if (copy) copy.hidden = !any;
}

/** «t = 3 с, x = 17 м» — момент и место; у скорости — момент и v. */
function momentText(time, tt, val) {
    return 't = ' + withUnit(tt, unitT()) + ', ' + headText(time) + ' = ' + withUnit(val, unitOf(time.role));
}

/** Строки ответа о движении: {label, text} или {label, pts} (точка-кнопка). */
function motionLines(g) {
    const mo = g.mo, time = g.time;
    const out = [];
    const idx = headText({ head: '', index: time.index });
    const vName = 'v' + idx;
    if (mo.type === 'nonuniform') {
        if (mo.role === 'vel' && fin(mo.v0)) out.push({ label: t('mo_v0'), text: 'v₀ = ' + withUnit(mo.v0, unitOf('vel')) });
        out.push({ label: t('mo_type'), text: t('mo_type_nonuniform') });
        return out;
    }
    if (mo.role === 'acc') {
        out.push({ label: t('mo_a'), text: headText(time) + ' = ' + withUnit(mo.a, unitOf('acc')) });
        return out;
    }
    // У высоты и пути — свои слова: «начальная координата h₀» читается как ошибка.
    const startKey = time.head === 'h' ? 'mo_start_h' : time.head === 's' ? 'mo_start_s' : 'mo_start';
    if (mo.role === 'coord') out.push({ label: t(startKey), text: headText(time, true) + ' = ' + withUnit(mo.x0, unitOf('coord')) });
    out.push({ label: t('mo_v0'), text: 'v₀' + idx + ' = ' + withUnit(mo.v0, unitOf('vel')) });
    out.push({ label: t('mo_a'), text: 'a' + idx + ' = ' + withUnit(mo.a, unitOf('acc')) });
    out.push({ label: t('mo_type'), text: t('mo_type_' + mo.type) });
    if (mo.role === 'coord') out.push({ label: t('mo_vlaw'), text: vName + ' = ' + linearLaw(mo.v0, mo.a, (v) => FMT.num(v, LOC)) });
    if (g.stop) {
        const p = Object.assign({}, g.stop, { text: momentText(mo.role === 'coord' ? time : { head: 'v', index: time.index, role: 'vel' }, g.stop.x, g.stop.y) });
        out.push({ label: mo.role === 'coord' ? t('mo_turn') : t('mo_stop'), pts: [p] });
        // Тормозной путь — второй вопрос той же задачи; по графику его искать — площадь.
        const brake = mo.role === 'coord' ? Math.abs(mo.stop.x - mo.x0) : (mo.v0 * mo.v0) / (2 * Math.abs(mo.a));
        if (fin(brake)) out.push({ label: t('mo_brake'), text: withUnit(Math.round(brake * 1e6) / 1e6, unitOf('coord')) });
    }
    return out;
}

/** Встреча двух тел или обычное пересечение. */
function meetLines(it) {
    if (!it.meet || !it.far) return [{ label: '', pts: it.pts }];
    if (it.same) return [{ label: '', text: t('mo_meet_same') }];
    if (!it.pts.length && !it.far.length) return [{ label: '', text: t('mo_meet_none') }];
    // В момент встречи координата у тел одна — «x = 20 м», без номера тела.
    const time = { head: it.a.b.time.head, index: '', role: 'coord' };
    const out = [];
    if (it.pts.length) out.push({ label: '', pts: it.pts.map((p) => Object.assign({}, p, { text: momentText(time, p.x, p.y) })) });
    for (const p of it.far) out.push({ label: '', text: momentText(time, p.x, p.y) + ' · ' + t('mo_offscreen') });
    return out;
}

/** «+ график скорости» к закону координаты, «+ график ускорения» к скорости. */
function companionRow(g) {
    const mo = g.mo;
    if (!mo || mo.type === 'nonuniform' || state.rows.filter((r) => r.src.trim()).length >= MAX_ROWS) return null;
    let src, what;
    const idx = g.time.index || '';
    if (mo.role === 'coord') { src = 'v' + idx + ' = ' + linearLaw(mo.v0, mo.a, inputNum); what = 'v'; }
    else if (mo.role === 'vel') { src = 'a' + idx + ' = ' + inputNum(mo.a); what = 'a'; }
    else return null;
    const norm = (s) => String(s).replace(/\s+/g, '').replace(/−/g, '-');
    // Уже есть такая величина у этого тела — второй кнопки не нужно.
    const head = what + idx;
    if (state.rows.some((r) => r.read && r.read.kind === 'motion' && r.read.head + (r.read.index || '') === head)) return null;
    if (state.rows.some((r) => norm(r.src) === norm(src))) return null;
    return { src, what, label: t(what === 'v' ? 'mo_add_v' : 'mo_add_a') };
}

function answersText() {
    const lines = [];
    for (const g of state.facts.groups) {
        lines.push(g.label);
        if (g.mo) for (const m of motionLines(g)) lines.push('  ' + m.label + ': ' + (m.pts ? m.pts.map((p) => p.text || FMT.point(p.x, p.y, LOC)).join(', ') : m.text));
        const known = g.mo && g.mo.type !== 'nonuniform';
        if (g.roots.length && !(known && g.mo.role === 'vel' && g.stop)) lines.push('  ' + (g.time ? headText(g.time) + ' = 0' : t('facts_roots')) + ': ' + g.roots.map((p) => FMT.point(p.x, p.y, LOC)).join(', '));
        if (g.yint && !known) lines.push('  ' + (g.time ? 't = 0' : t('facts_yint')) + ': ' + FMT.point(g.yint.x, g.yint.y, LOC));
        for (const e of g.ext) lines.push('  ' + (e.kind === 'max' ? t('facts_max') : t('facts_min')) + ': ' + FMT.point(e.x, e.y, LOC));
        const va = g.brk.filter((b) => b.kind === 'asymptote');
        if (va.length) lines.push('  ' + t('facts_vasym') + ': ' + va.map((b) => 'x = ' + FMT.num(b.x, LOC)).join(', '));
        for (const a of g.inf) lines.push('  ' + t('facts_asym') + ': ' + FMT.line(a.k, a.b, LOC));
    }
    for (const it of state.facts.inter) {
        lines.push((it.meet ? t('facts_meet') : t('facts_inter')) + ': ' + it.a.b.label + ' ∩ ' + it.b.b.label);
        for (const m of meetLines(it)) lines.push('  ' + (m.pts ? m.pts.map((p) => p.text || FMT.point(p.x, p.y, LOC)).join(', ') : m.text));
    }
    return lines.join('\n');
}

function selectPoi(id) {
    const p = state.facts.poi.find((q) => q.id === id);
    if (!p) return;
    state.selected = { ...p, label: FMT.point(p.x, p.y, LOC) };
    document.querySelectorAll('.fp-pt[aria-pressed]').forEach((b) => b.removeAttribute('aria-pressed'));
    document.querySelectorAll('.fp-pt').forEach((b) => { if (b.dataset.poi === id) b.setAttribute('aria-pressed', 'true'); });
    announce(state.selected.label);
    schedule();
}

function announce(msg) { try { if (window.__announce) window.__announce(msg); } catch (_) {} }

/* ── исследование ────────────────────────────────────────────────────────── */
function studyRow() {
    const live = explicitRows();
    let row = live.find((r) => r.uid === state.studyUid);
    if (!row) row = live[0] || null;
    state.studyUid = row ? row.uid : null;
    return row;
}

function renderStudyPicker() {
    const sel = $('fpStudyRow');
    if (!sel) return;
    const live = explicitRows();
    const cur = studyRow();
    sel.textContent = '';
    for (const r of live) {
        const o = el('option', null, r.b.label);
        o.value = String(r.uid);
        if (cur && r.uid === cur.uid) o.selected = true;
        sel.append(o);
    }
    sel.disabled = live.length < 2;
    const sub = $('fpStudySub');
    if (sub) sub.textContent = cur ? cur.b.label : '';
}

function renderStudy() {
    const drawer = $('fpStudyDrawer');
    const box = $('fpStudy');
    if (!box || !drawer || !drawer.open) return;
    renderStudyPicker();
    box.textContent = '';
    const row = studyRow();
    $('fpStudyTools').hidden = !row;
    if (!row) { box.append(el('p', 'tc-hint', t('study_pick'))); return; }
    const f = row.b.f;
    const v = state.view;
    const g = state.facts.groups.find((q) => q.uid === row.uid);
    const brk = g ? g.brk : breaks(f, v.x0, v.x1);
    const dom = domain(f, v.x0, v.x1, brk);
    const rs = g ? g.roots.map((p) => p.x) : roots(f, v.x0, v.x1);
    const ex = g ? g.ext.map((p) => p.x) : [];
    const cutsB = brk.map((b) => b.x);
    const sign = intervals(dom, rs.concat(cutsB), (m) => Math.sign(f(m)));
    const mono = intervals(dom, ex.concat(cutsB), (m) => { const d = deriv(f, m); return fin(d) && Math.abs(d) > 1e-9 ? Math.sign(d) : 0; });
    const par = parity(f);
    const per = period(f, hasTrig(row.read.ast), /floor|ceil|round/.test(JSON.stringify(row.read.ast)));
    const infl = inflections(f, v.x0, v.x1, brk).slice(0, 8);
    const none = t('study_empty');
    const dl = el('dl', 'fp-study');
    const item = (label, text) => { dl.append(el('dt', null, label), el('dd', null, text || none)); };
    item(t('study_domain'), FMT.union(dom, LOC));
    item(t('study_parity'), par ? t('parity_' + par) : none);
    item(t('study_period'), per ? periodText(per) : t('period_none'));
    item(t('study_pos'), FMT.union(sign.filter((s) => s.sign > 0), LOC));
    item(t('study_neg'), FMT.union(sign.filter((s) => s.sign < 0), LOC));
    item(t('study_inc'), FMT.union(mono.filter((s) => s.sign > 0), LOC));
    item(t('study_dec'), FMT.union(mono.filter((s) => s.sign < 0), LOC));
    item(t('study_infl'), infl.map((p) => FMT.point(p.x, snapZero(p.y, v.y1 - v.y0), LOC)).join(', '));
    box.append(dl);
    renderTangent();
    renderIntegral();
}

function periodText(p) {
    if (p.q) {
        const k = p.p, q = p.q;
        const g = gcd(k, q);
        const num = k / g, den = q / g;
        return (num === 1 ? '' : num) + 'π' + (den === 1 ? '' : '/' + den);
    }
    return FMT.num(p.T, LOC);
}
function gcd(a, b) { return b ? gcd(b, a % b) : a; }

function tangentScene() {
    const drawer = $('fpStudyDrawer');
    if (!drawer || !drawer.open) return null;
    const row = studyRow();
    const x0 = constOf(($('fpTanX') || {}).value);
    if (!row || !fin(x0)) return null;
    const tg = tangent(row.b.f, x0);
    return tg ? { ...tg, color: row.color } : null;
}

function renderTangent() {
    const out = $('fpTanOut');
    if (!out) return;
    const tg = tangentScene();
    const raw = ($('fpTanX') || {}).value;
    if (!tg) { out.textContent = String(raw || '').trim() ? t('study_empty') : t('tan_hint'); return; }
    out.textContent = FMT.line(snapZero(tg.k, 1), snapZero(tg.b, 1), LOC) + '   f′(' + FMT.num(tg.x0, LOC) + ') = ' + FMT.num(snapZero(tg.k, 1), LOC);
}

function integralScene() {
    const drawer = $('fpStudyDrawer');
    if (!drawer || !drawer.open) return null;
    const row = studyRow();
    const a = constOf(($('fpIntA') || {}).value);
    const b = constOf(($('fpIntB') || {}).value);
    if (!row || !fin(a) || !fin(b) || a === b) return null;
    return { f: row.b.f, a, b, color: row.color };
}

function renderIntegral() {
    const out = $('fpIntOut');
    if (!out) return;
    const I = integralScene();
    if (!I) { out.textContent = ''; return; }
    const g = state.facts.groups.find((q) => q.uid === state.studyUid);
    const lo = Math.min(I.a, I.b), hi = Math.max(I.a, I.b);
    const brk = g && lo >= state.view.x0 && hi <= state.view.x1 ? g.brk : breaks(I.f, lo - 1e-9, hi + 1e-9);
    const res = integral(I.f, I.a, I.b, brk);
    if (!res.ok) { out.textContent = res.reason === 'asymptote' ? t('int_asym') : t('int_undef'); return; }
    // Площадь под графиком СКОРОСТИ — это путь, а интеграл — перемещение: так
    // и спрашивает задача «найдите путь по графику», и с единицами длины.
    const row = studyRow();
    if (row && row.b.time && row.b.time.role === 'vel') {
        const d = unitOf('coord');
        out.textContent = t('mo_disp') + ' = ' + withUnit(snapZero(res.value, 1e-3), d) + '   ' + t('mo_path') + ' = ' + withUnit(snapZero(res.area, 1e-3), d);
        return;
    }
    out.textContent = t('int_value') + ' = ' + FMT.num(snapZero(res.value, 1e-3), LOC) + '   ' + t('int_area') + ' = ' + FMT.num(snapZero(res.area, 1e-3), LOC);
}

/* ── таблица значений ────────────────────────────────────────────────────── */
function tableStep() {
    const raw = Number(String(($('fpStep') || {}).value || '').replace(',', '.'));
    if (raw > 0) return raw;
    return niceStep(state.view.x1 - state.view.x0, 20 * 64);
}

function tableData() {
    const live = explicitRows();
    if (!live.length) return null;
    const v = state.view;
    const step = tableStep();
    const x0 = Math.ceil(v.x0 / step - 1e-9) * step;
    const header = [axisVar()].concat(live.map((r) => r.b.label));
    const rows = table(live[0].b.f, x0, v.x1, step).map((p) => {
        const x = round(p.x);
        return [Math.abs(x) < 1e-12 ? 0 : x].concat(live.map((r) => r.b.f(x)));
    });
    return { header, rows, live, step };
}

function renderTable() {
    const drawer = $('fpTableDrawer');
    const box = $('fpTable');
    if (!box || !drawer || !drawer.open) return;
    box.textContent = '';
    const d = tableData();
    const more = $('fpTableMore');
    if (!d) { if (more) more.hidden = true; return; }
    const tbl = el('table', 'tc-table fp-table');
    const thead = el('thead');
    const hr = el('tr');
    d.header.forEach((hname, i) => {
        const th = el('th', null, i ? 'f' + i : d.header[0]);
        if (i) { th.title = hname; th.style.color = d.live[i - 1].color; }
        hr.append(th);
    });
    thead.append(hr);
    const tb = el('tbody');
    const LIMIT = 41;
    for (const r of d.rows.slice(0, LIMIT)) {
        const tr = el('tr');
        r.forEach((val) => tr.append(el('td', 'num', fin(val) ? FMT.num(val, LOC) : '—')));
        tb.append(tr);
    }
    tbl.append(thead, tb);
    box.append(tbl);
    if (more) {
        more.hidden = d.rows.length <= LIMIT;
        more.textContent = t('table_more').replace(':n', String(LIMIT));
    }
}

/* ── ввод ────────────────────────────────────────────────────────────────── */
const KIND_KEY = { ineq: 'kind_ineq', xofy: 'kind_xofy', xineq: 'kind_ineq', polar: 'kind_polar', param: 'kind_param', points: 'kind_points', implicit: 'kind_implicit', motion: 'kind_motion' };

function renderInputs() {
    const box = $('fpInputs');
    if (!box) return;
    box.textContent = '';
    state.rows.forEach((row, i) => {
        const wrap = el('div', 'fp-row');
        wrap.dataset.uid = String(row.uid);
        wrap.style.setProperty('--c', row.color);
        const line1 = el('div', 'fp-line');
        const dot = el('button', 'fp-dot');
        dot.type = 'button';
        dot.setAttribute('aria-pressed', row.hidden ? 'false' : 'true');
        dot.setAttribute('aria-label', t('toggle_fn') + ' ' + (i + 1));
        dot.title = t('toggle_fn');
        dot.addEventListener('click', () => {
            row.hidden = !row.hidden;
            dot.setAttribute('aria-pressed', row.hidden ? 'false' : 'true');
            refresh(false);
        });
        const inp = el('input', 'fp-in');
        inp.type = 'text';
        // Формула читается слева направо и в арабском интерфейсе: курсор и
        // выделение в строке «x = 5 + 4t» иначе прыгают с края на край.
        inp.dir = 'ltr';
        inp.value = row.src;
        inp.spellcheck = false;
        inp.autocomplete = 'off';
        inp.setAttribute('autocapitalize', 'off');
        inp.setAttribute('enterkeyhint', 'done');
        inp.id = 'fpFn' + i;
        inp.setAttribute('aria-label', 'f' + (i + 1));
        inp.placeholder = t('placeholder');
        inp.addEventListener('input', () => { row.src = inp.value; refresh(true); });
        inp.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.isComposing && state.rows.length < MAX_ROWS && i === state.rows.length - 1 && inp.value.trim()) {
                e.preventDefault();
                addRow('');
                renderInputs();
                focusRow(state.rows.length - 1);
            }
        });
        const del = el('button', 'fp-x', '×');
        del.type = 'button';
        del.title = t('remove');
        del.setAttribute('aria-label', t('remove') + ' ' + (i + 1));
        del.addEventListener('click', () => {
            state.rows.splice(state.rows.indexOf(row), 1);
            if (!state.rows.length) addRow('');
            if (state.selected && String(state.selected.id).startsWith(row.uid + ':')) state.selected = null;
            renderInputs();
            refresh(false);
        });
        line1.append(dot, inp, del);
        const sub = el('div', 'fp-sub');
        sub.hidden = true;
        const kind = el('span', 'fp-kind');
        const range = el('span', 'fp-trange');
        range.hidden = true;
        const mk = (key, label) => {
            const lab = el('label', 'fp-tf');
            lab.append(el('span', null, label));
            const f = el('input', 'fp-tin');
            f.type = 'text';
            f.dir = 'ltr';
            f.value = row[key];
            f.inputMode = 'text';
            f.spellcheck = false;
            f.setAttribute('aria-label', label);
            f.addEventListener('input', () => { row[key] = f.value; schedule(); saveHashSoon(); });
            lab.append(f);
            return lab;
        };
        range.append(mk('t0', t('t_from')), mk('t1', t('t_to')));
        sub.append(kind, range);
        wrap.append(line1, sub);
        box.append(wrap);
    });
    const add = $('fpAdd');
    if (add) add.hidden = state.rows.length >= MAX_ROWS;
    updateRowMeta();
}

function focusRow(i) {
    const inp = $('fpFn' + i);
    if (inp) inp.focus();
}

/** Подпись вида кривой и красная рамка — без перерисовки полей (фокус не теряется). */
function updateRowMeta() {
    for (const row of state.rows) {
        const wrap = document.querySelector('.fp-row[data-uid="' + row.uid + '"]');
        if (!wrap) continue;
        const r = row.read || { kind: 'empty' };
        const inp = wrap.querySelector('.fp-in');
        const bad = r.kind === 'error';
        if (inp) {
            if (bad) inp.setAttribute('aria-invalid', 'true'); else inp.removeAttribute('aria-invalid');
        }
        const sub = wrap.querySelector('.fp-sub');
        const kind = wrap.querySelector('.fp-kind');
        const range = wrap.querySelector('.fp-trange');
        const key = KIND_KEY[r.kind];
        const parts = [];
        if (r.kind === 'assign') parts.push(t('kind_assign').replace(':name', paramText(r.name)));
        else if (key) parts.push(t(key));
        // Пояснение словами отрезано — человек обязан это видеть, а не гадать,
        // почему «где t в минутах» ничего не поменяло.
        if (r.note) parts.push(t('note_skipped').replace(':note', r.note));
        if (sub) sub.hidden = !parts.length;
        if (kind) kind.textContent = parts.join(' · ');
        if (range) range.hidden = !(r.kind === 'polar' || r.kind === 'param');
    }
}

function errorText(row) {
    const e = row.read.err || {};
    const code = String(e.code || '');
    let msg = t('err_parse');
    let m;
    if ((m = /^unknown_name:(.+)$/.exec(code))) msg = t('err_unknown').replace(':name', m[1]);
    else if (/^expected_(paren|bar)/.test(code)) msg = t('err_paren');
    else if (code === 'one_relation') msg = t('err_one_relation');
    else if (code === 'one_curve_per_row') msg = t('err_one_curve');
    const where = e.at != null ? ' · ' + t('err_at') + ' ' + (e.at + 1) : '';
    return msg + ': ' + row.src + where;
}

function renderErrors() {
    const box = $('fpErrors');
    if (!box) return;
    box.textContent = '';
    const bad = state.rows.filter((r) => r.read && r.read.kind === 'error');
    box.hidden = !bad.length;
    for (const row of bad) {
        const p = el('p', 'fp-err', errorText(row));
        p.style.setProperty('--c', row.color);
        box.append(p);
    }
}

/* ── параметры ───────────────────────────────────────────────────────────── */
function renderParams() {
    const box = $('fpParams');
    if (!box) return;
    box.textContent = '';
    box.hidden = !state.paramOrder.length;
    for (const name of state.paramOrder) {
        const p = state.params[name];
        const row = el('div', 'fp-par');
        row.dataset.par = name;
        const head = el('div', 'fp-par-h');
        const lab = el('label', 'fp-par-n');
        lab.htmlFor = 'fpParV_' + name;
        lab.textContent = paramText(name) + ' =';
        const val = el('input', 'fp-par-v');
        val.type = 'text';
        val.inputMode = 'decimal';
        val.dir = 'ltr';
        val.id = 'fpParV_' + name;
        val.value = FMT.num(p.v, LOC).replace('−', '-');
        const play = el('button', 'fp-par-play');
        play.type = 'button';
        const setPlayUi = () => {
            const on = state.playing.has(name);
            play.textContent = on ? '❚❚' : '▶';
            play.setAttribute('aria-pressed', on ? 'true' : 'false');
            play.setAttribute('aria-label', (on ? t('param_pause') : t('param_play')) + ' ' + name);
            play.title = on ? t('param_pause') : t('param_play');
        };
        setPlayUi();
        play.addEventListener('click', () => {
            if (state.playing.has(name)) state.playing.delete(name); else { state.playing.add(name); ae('param_play', {}); }
            setPlayUi();
            loop();
        });
        head.append(lab, val, play);
        const body = el('div', 'fp-par-b');
        const lim = (key) => {
            const f = el('input', 'fp-par-lim');
            f.type = 'text';
            f.inputMode = 'decimal';
            f.dir = 'ltr';
            f.value = FMT.num(p[key], LOC).replace('−', '-');
            f.setAttribute('aria-label', name + ' ' + t(key === 'min' ? 'param_min' : 'param_max'));
            f.addEventListener('change', () => {
                const n = constOf(f.value);
                if (!fin(n)) { f.value = FMT.num(p[key], LOC).replace('−', '-'); return; }
                p[key] = n;
                if (p.min > p.max) { const s = p.min; p.min = p.max; p.max = s; }
                range.min = String(p.min); range.max = String(p.max);
                range.step = String(sliderStep(p));
                saveHashSoon();
            });
            return f;
        };
        const range = el('input', 'fp-par-r');
        range.type = 'range';
        range.min = String(p.min);
        range.max = String(p.max);
        range.step = String(sliderStep(p));
        range.value = String(p.v);
        range.setAttribute('aria-label', name);
        range.addEventListener('input', () => {
            setParam(name, Number(range.value), { fromRange: true });
            if (!paramTouched) { paramTouched = true; ae('param_slider', {}); }
        });
        val.addEventListener('change', () => {
            const n = constOf(val.value);
            if (!fin(n)) { val.value = FMT.num(p.v, LOC).replace('−', '-'); return; }
            if (n < p.min) p.min = n;
            if (n > p.max) p.max = n;
            range.min = String(p.min); range.max = String(p.max);
            setParam(name, n);
        });
        body.append(lim('min'), range, lim('max'));
        row.append(head, body);
        box.append(row);
    }
}
let paramTouched = false;

function sliderStep(p) {
    const span = Math.abs(p.max - p.min) || 1;
    return niceStep(span, 200 * 64) || 0.01;
}

function setParam(name, value, opts) {
    const p = state.params[name];
    if (!p) return;
    p.v = value;
    ENV[name] = value;
    const row = document.querySelector('.fp-par[data-par="' + name + '"]');
    if (row) {
        const val = row.querySelector('.fp-par-v');
        if (val && document.activeElement !== val) val.value = FMT.num(round(value), LOC).replace('−', '-');
        const range = row.querySelector('.fp-par-r');
        if (range && !(opts && opts.fromRange)) range.value = String(value);
    }
    if (state.auto) autoY();
    schedule();
    factsSoon(opts && opts.anim ? 260 : 120);
    saveHashSoon();
}

let lastTick = 0;
let loopId = 0;
function loop() {
    if (loopId || !state.playing.size) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    lastTick = performance.now();
    const step = (now) => {
        loopId = 0;
        if (!state.playing.size) return;
        const dt = Math.min(0.1, (now - lastTick) / 1000);
        lastTick = now;
        for (const name of state.playing) {
            const p = state.params[name];
            if (!p) continue;
            const span = p.max - p.min || 1;
            // один проход от края до края за 4 секунды (при сниженном движении — за 12)
            p.dir = p.dir || 1;
            let v = p.v + p.dir * span * dt / (reduce ? 12 : 4);
            if (v > p.max) { v = p.max; p.dir = -1; }
            if (v < p.min) { v = p.min; p.dir = 1; }
            setParam(name, v, { anim: true });
        }
        loopId = requestAnimationFrame(step);
    };
    loopId = requestAnimationFrame(step);
}

/* ── обновление ──────────────────────────────────────────────────────────── */
let factTimer = null;
function factsSoon(ms) {
    clearTimeout(factTimer);
    factTimer = setTimeout(() => {
        computeFacts();
        renderFacts();
        renderStudy();
        renderTable();
        schedule();
    }, ms);
}

let hashTimer = null;
function saveHashSoon() {
    clearTimeout(hashTimer);
    hashTimer = setTimeout(() => {
        saveHash();
        if (window.__toolSettings) {
            window.__toolSettings.save(SLUG, 'fns', state.rows.map((r) => r.src));
        }
    }, 250);
}

const kindsSeen = new Set();
function refresh(fromInput) {
    readAll();
    syncUnitsUi();
    // Вид, которого человек не трогал, следует за тем, что на поле: появился закон
    // движения — ось от t = 0; ушёл — обратно симметричная. Сдвинутый вид не трогаем.
    if (!state.viewTouched) {
        const want = homeView();
        state.view.x0 = want.x0; state.view.x1 = want.x1;
    }
    if (state.auto) autoY();
    updateRowMeta();
    renderErrors();
    renderStudyPicker();
    schedule();
    saveHashSoon();
    const live = state.rows.filter((r) => r.b);
    const bad = state.rows.filter((r) => r.read && r.read.kind === 'error');
    if (live.length) stage('plotted', { n: live.length });
    for (const r of live) {
        const kind = r.read.kind;
        if (kind !== 'explicit' && !kindsSeen.has(kind)) { kindsSeen.add(kind); ae('row_kind', { kind }); }
    }
    if (fromInput && bad.length) {
        const e = bad[0].read.err;
        stage('error', { reason: badChar(e.code), at: e.at });
        ae('parse_failed', { ch: badChar(e.code), at: e.at, cls: rowClass(bad[0].src) });
    }
    if (live.some((r) => r.read.note) && !kindsSeen.has('note')) {
        kindsSeen.add('note');
        ae('comment_cut', { units: unitsInNote(live.find((r) => r.read.note).read.note) || '' });
    }
    // Поиск нулей и асимптот дороже рисования: при наборе он ждёт паузы.
    factsSoon(fromInput ? 350 : 0);
}

/* ── холст: жесты, колесо, клавиши ───────────────────────────────────────── */
function bindCanvas() {
    const cvs = $('fpCanvas');
    if (!cvs) return;
    const ptrs = new Map();
    let gest = null;
    const local = (e) => { const r = cvs.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top, w: r.width, h: r.height }; };

    cvs.addEventListener('pointerdown', (e) => {
        try { cvs.setPointerCapture(e.pointerId); } catch (_) {}
        const p = local(e);
        ptrs.set(e.pointerId, p);
        hideHint();
        if (ptrs.size === 1) gest = { type: 'pan', sx: p.x, sy: p.y, view: { ...state.view }, moved: false, touch: e.pointerType !== 'mouse' };
        else if (ptrs.size === 2) {
            const [a, b] = [...ptrs.values()];
            gest = { type: 'pinch', d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2, view: { ...state.view }, moved: true };
        }
    });
    cvs.addEventListener('pointermove', (e) => {
        const p = local(e);
        if (!ptrs.has(e.pointerId)) {
            if (e.pointerType === 'mouse') {
                state.hover = { x: viewMap(state.view, p.w, p.h).px2x(p.x) };
                showTrace();
                schedule();
            }
            return;
        }
        ptrs.set(e.pointerId, p);
        if (!gest) return;
        const v0 = gest.view;
        if (gest.type === 'pan') {
            const dx = p.x - gest.sx, dy = p.y - gest.sy;
            if (!gest.moved && Math.hypot(dx, dy) < (gest.touch ? 8 : 4)) return;
            gest.moved = true;
            const kx = (v0.x1 - v0.x0) / p.w, ky = (v0.y1 - v0.y0) / p.h;
            state.view = { x0: v0.x0 - dx * kx, x1: v0.x1 - dx * kx, y0: v0.y0 + dy * ky, y1: v0.y1 + dy * ky };
            state.viewTouched = true;
            if (Math.abs(dy) > 2) setAuto(false);
            state.hover = null;
            showTrace();
            schedule();
        } else if (gest.type === 'pinch' && ptrs.size >= 2) {
            const [a, b] = [...ptrs.values()];
            const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
            const k = gest.d0 / d;
            const cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
            const M0 = viewMap(v0, p.w, p.h);
            const wx = M0.px2x(gest.cx), wy = M0.py2y(gest.cy);
            const nw = (v0.x1 - v0.x0) * k, nh = (v0.y1 - v0.y0) * k;
            const x0 = wx - (cx / p.w) * nw;
            const y1 = wy + (cy / p.h) * nh;
            state.view = { x0, x1: x0 + nw, y0: y1 - nh, y1 };
            state.viewTouched = true;
            setAuto(false);
            schedule();
        }
    });
    const end = (e) => {
        const p = local(e);
        ptrs.delete(e.pointerId);
        if (!gest) return;
        if (gest.type === 'pan' && !gest.moved && ptrs.size === 0) tap(p, gest.touch);
        if (ptrs.size === 0) {
            const was = gest;
            gest = null;
            if (was.moved) { if (state.auto) autoY(); refresh(false); ae('pan', {}); }
        } else if (ptrs.size === 1 && gest.type === 'pinch') {
            const [q] = [...ptrs.values()];
            gest = { type: 'pan', sx: q.x, sy: q.y, view: { ...state.view }, moved: true, touch: true };
        }
    };
    cvs.addEventListener('pointerup', end);
    cvs.addEventListener('pointercancel', end);
    cvs.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse' && !ptrs.size) { state.hover = null; showTrace(); schedule(); }
    });

    cvs.addEventListener('wheel', (e) => {
        e.preventDefault();
        hideHint();
        const p = local(e);
        const M = viewMap(state.view, p.w, p.h);
        const k = Math.pow(1.0018, e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY);
        zoomAt(M.px2x(p.x), M.py2y(p.y), Math.min(4, Math.max(0.25, k)));
    }, { passive: false });

    cvs.addEventListener('keydown', (e) => {
        const v = state.view;
        const dx = (v.x1 - v.x0) * 0.1, dy = (v.y1 - v.y0) * 0.1;
        let handled = true;
        switch (e.key) {
            case 'ArrowLeft': shift(-dx, 0); break;
            case 'ArrowRight': shift(dx, 0); break;
            case 'ArrowUp': shift(0, dy); setAuto(false); break;
            case 'ArrowDown': shift(0, -dy); setAuto(false); break;
            case '+': case '=': zoomAt(mid('x'), mid('y'), 1 / 1.3); break;
            case '-': case '_': zoomAt(mid('x'), mid('y'), 1.3); break;
            case 'Escape': state.selected = null; renderFacts(); schedule(); break;
            default: handled = false;
        }
        // «0» — по месту клавиши (AZERTY без Shift печатает «à»); знаки выше — как напечатаны.
        if (!handled && !e.ctrlKey && !e.metaKey && !e.altKey && (window.__keyLetter ? window.__keyLetter(e) : e.key) === '0') { resetView(); handled = true; }
        if (handled) e.preventDefault();
    });
}

function shift(dx, dy) {
    const v = state.view;
    state.view = { x0: v.x0 + dx, x1: v.x1 + dx, y0: v.y0 + dy, y1: v.y1 + dy };
    state.viewTouched = true;
    refresh(false);
}

function tap(p, touch) {
    const M = viewMap(state.view, p.w, p.h);
    const R = touch ? 24 : 14;
    let best = null;
    let bd = R;
    for (const q of state.facts.poi) {
        const d = Math.hypot(M.sx(q.x) - p.x, M.sy(q.y) - p.y);
        if (d < bd) { bd = d; best = q; }
    }
    if (best) { selectPoi(best.id); renderFacts(); ae('poi_click', { from: 'canvas', what: poiWhat(best.id) }); return; }
    const x = M.px2x(p.x);
    for (const row of explicitRows()) {
        const y = row.b.f(x);
        if (!fin(y)) continue;
        const d = Math.abs(M.sy(y) - p.y);
        if (d < bd) { bd = d; best = { id: 'curve', uid: row.uid, x, y, color: row.color }; }
    }
    if (best) {
        const span = state.view.x1 - state.view.x0;
        const x2 = round(snapZero(best.x, span));
        state.selected = { ...best, x: x2, y: best.y, label: FMT.point(x2, snapZero(best.y, state.view.y1 - state.view.y0), LOC) };
        announce(state.selected.label);
        if (touch) { state.hover = { x: x2 }; showTrace(); }
        const drawer = $('fpStudyDrawer');
        if (drawer && drawer.open) {
            state.studyUid = best.uid;
            const tx = $('fpTanX');
            if (tx) tx.value = FMT.num(x2, LOC).replace('−', '-');
            renderStudy();
        }
        ae('curve_point', {});
    } else {
        state.selected = null;
    }
    renderFacts();
    schedule();
}

function zoomAt(cx, cy, k) {
    const v = state.view;
    const nx = (v.x1 - v.x0) * k;
    if (nx < 1e-9 || nx > 1e9) return;
    state.view = { x0: cx + (v.x0 - cx) * k, x1: cx + (v.x1 - cx) * k, y0: cy + (v.y0 - cy) * k, y1: cy + (v.y1 - cy) * k };
    state.viewTouched = true;
    setAuto(false);
    refresh(false);
}

function mid(axis) {
    const v = state.view;
    return axis === 'x' ? (v.x0 + v.x1) / 2 : (v.y0 + v.y1) / 2;
}

/** Исходный вид: у законов движения — от t = 0, у остального — симметричный. */
function homeView() { return allMotion() ? MOTION_HOME : HOME; }

function resetView() {
    state.view = { ...homeView() };
    state.viewTouched = false;
    setAuto(true);
    refresh(false);
}

function showTrace() {
    const box = $('fpTrace');
    if (!box) return;
    if (!state.hover) { box.textContent = ''; return; }
    const span = state.view.x1 - state.view.x0;
    const x = state.hover.x;
    const parts = [(allMotion() ? 't' : t('trace_x')) + ' = ' + FMT.num(snapZero(x, span), LOC)];
    state.rows.forEach((row, i) => {
        if (!row.b || row.hidden || row.b.kind !== 'explicit') return;
        parts.push('f' + (i + 1) + ' = ' + FMT.num(snapZero(row.b.f(x), state.view.y1 - state.view.y0), LOC));
    });
    box.textContent = parts.join('   ');
}

function hideHint() {
    const hint = $('fpHint');
    if (hint && !hint.hidden) {
        hint.hidden = true;
        try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'hinted', 1); } catch (_) {}
    }
}

/* ── клавиатура для телефона ─────────────────────────────────────────────── */
function bindKeypad() {
    const pad = $('fpKeypad');
    const list = $('fpInputs');
    if (!pad || !list) return;
    const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)');
    let target = null;
    list.addEventListener('focusin', (e) => {
        if (!e.target.classList || !e.target.classList.contains('fp-in')) return;
        target = e.target;
        if (coarse && coarse.matches) pad.hidden = false;
    });
    list.addEventListener('focusout', (e) => {
        if (pad.contains(e.relatedTarget)) return;
        setTimeout(() => { if (!list.contains(document.activeElement) && !pad.contains(document.activeElement)) pad.hidden = true; }, 150);
    });
    // Нажатие по клавише не должно забирать фокус у поля: иначе системная
    // клавиатура прячется и выезжает заново на каждый знак.
    pad.addEventListener('pointerdown', (e) => { if (e.target.closest('[data-k]')) e.preventDefault(); });
    let used = false;
    pad.addEventListener('click', (e) => {
        const key = e.target.closest('[data-k]');
        if (!key || !target) return;
        const k = key.getAttribute('data-k');
        const s = target.selectionStart ?? target.value.length;
        const f = target.selectionEnd ?? s;
        if (k === '←' || k === '→') {
            const pos = Math.max(0, Math.min(target.value.length, s + (k === '←' ? -1 : 1)));
            target.setSelectionRange(pos, pos);
        } else if (k === '⌫') {
            const a = s === f ? Math.max(0, s - 1) : s;
            target.value = target.value.slice(0, a) + target.value.slice(f);
            target.setSelectionRange(a, a);
            target.dispatchEvent(new Event('input', { bubbles: true }));
        } else {
            const text = key.getAttribute('data-ins') || k;
            const back = Number(key.getAttribute('data-back') || 0);
            target.value = target.value.slice(0, s) + text + target.value.slice(f);
            const pos = s + text.length - back;
            target.setSelectionRange(pos, pos);
            target.dispatchEvent(new Event('input', { bubbles: true }));
        }
        target.focus();
        if (!used) { used = true; ae('keypad', {}); }
    });
}

/* ── выдача ──────────────────────────────────────────────────────────────── */
function renderTo(w, h, scale, light) {
    const out = document.createElement('canvas');
    out.width = Math.round(w * scale);
    out.height = Math.round(h * scale);
    const c = out.getContext('2d');
    c.scale(scale, scale);
    paint(c, buildScene(w, h, { background: true, legend: true, hover: null, theme: themeColors(light) }));
    return out;
}

function exportPng() {
    if (!window.__downloadFile) return;
    const { w, h } = canvasSize();
    // Фон нужен: прозрачный PNG в мессенджере ложится на тёмное и исчезает.
    const out = renderTo(w, h, 2, false);
    out.toBlob((blob) => { if (blob) window.__downloadFile(blob, name('png')); }, 'image/png');
    ae('export_png', { motion: allMotion() ? 1 : 0 });
    complete('png');
}

function exportCsv() {
    const d = tableData();
    if (!d) { toast(t('study_pick'), 'info'); return; }
    const text = FMT.csv(d.header, d.rows, LOC);
    if (window.__downloadFile) window.__downloadFile(new Blob([text], { type: 'text/csv;charset=utf-8' }), name('csv'));
    ae('export_csv', { step: d.step, motion: allMotion() ? 1 : 0 });
    complete('csv');
}

async function copyText(text) {
    try {
        if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return true; }
    } catch (_) { /* ниже — старый путь */ }
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:absolute;left:-9999px;top:0';
        document.body.append(ta);
        ta.select();
        const ok = document.execCommand('copy');
        ta.remove();
        return ok;
    } catch (_) { return false; }
}

async function copyLink() {
    saveHash();
    const ok = await copyText(location.href);
    toast(ok ? t('link_copied') : t('copy_failed'), ok ? 'success' : 'warning');
    ae('copy_link', { ok: ok ? 1 : 0 });
}

async function shareLink() {
    saveHash();
    try {
        await navigator.share({ title: document.title, url: location.href });
        ae('share', { ok: 1 });
    } catch (e) {
        if (e && e.name === 'AbortError') return;
        copyLink();
    }
}

async function copyImage() {
    const { w, h } = canvasSize();
    const out = renderTo(w, h, 2, false);
    try {
        // Safari требует, чтобы запись в буфер началась в том же жесте, поэтому
        // в ClipboardItem уходит ОБЕЩАНИЕ картинки, а не готовая картинка.
        const blob = new Promise((res, rej) => out.toBlob((b) => (b ? res(b) : rej(new Error('blob'))), 'image/png'));
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        toast(t('img_copied'), 'success');
        ae('copy_png', { ok: 1 });
        complete('clipboard');
    } catch (_) {
        toast(t('copy_failed'), 'warning');
        ae('copy_png', { ok: 0 });
    }
}

/** Признак — `is_pro`, поле, которое layout действительно публикует. */
function isPro() {
    try { if (window.__tierLimits && typeof window.__tierLimits.is_pro === 'boolean') return window.__tierLimits.is_pro; } catch (_) {}
    return false;
}

async function exportPdf() {
    if (!isPro()) {
        const box = $('fpPerk');
        if (box) box.hidden = false;
        ae('perk_upsell', { what: 'pdf_sheet' });
        return;
    }
    if (typeof window.__generatePDF !== 'function') { toast(t('copy_failed'), 'warning'); return; }
    const btn = $('fpPdf');
    if (btn) btn.disabled = true;
    try {
        if (!state.facts.groups.length && explicitRows().length) computeFacts();
        const sheet = await buildSheet();
        const res = await window.__generatePDF({ element: sheet, filename: name('pdf'), title: t('pdf_title') });
        if (res && res.ok) { ae('export_pdf', { motion: allMotion() ? 1 : 0 }); complete('pdf'); }
    } finally {
        if (btn) btn.disabled = false;
    }
}

/** Лист для печати: график на белом, ответы, исследование, таблица. */
async function buildSheet() {
    const sheet = el('div', 'fp-sheet');
    sheet.style.cssText = 'width:760px;padding:8px 4px;background:#fff;color:#111827;font:14px/1.5 system-ui,sans-serif';
    const W = 752, H = 480;
    const img = new Image();
    img.src = renderTo(W, H, 2, true).toDataURL('image/png');
    img.width = W;
    img.height = H;
    img.style.cssText = 'display:block;width:' + W + 'px;height:' + H + 'px;border:1px solid #d5dae1;border-radius:6px';
    try { await img.decode(); } catch (_) { /* html2canvas дождётся сам */ }
    sheet.append(img);
    const h = (txt) => { const n = el('h3', null, txt); n.style.cssText = 'font-size:16px;margin:16px 0 6px'; return n; };
    const pre = (txt) => { const n = el('pre', null, txt); n.style.cssText = 'white-space:pre-wrap;font:13px/1.55 ui-monospace,Menlo,monospace;margin:0'; return n; };
    const ans = answersText();
    if (ans) sheet.append(h(t('pdf_answers')), pre(ans));
    const drawer = $('fpStudyDrawer');
    const wasOpen = drawer && drawer.open;
    const lines = [];
    for (const row of explicitRows().slice(0, 4)) {
        state.studyUid = row.uid;
        if (drawer) drawer.open = true;
        renderStudy();
        lines.push(row.b.label);
        document.querySelectorAll('#fpStudy dt').forEach((dt) => {
            lines.push('  ' + dt.textContent + ': ' + (dt.nextElementSibling ? dt.nextElementSibling.textContent : ''));
        });
    }
    if (drawer) { drawer.open = wasOpen; renderStudy(); }
    if (lines.length) sheet.append(h(t('pdf_study')), pre(lines.join('\n') + '\n\n' + t('study_note')));
    const d = tableData();
    if (d) {
        const rows = d.rows.slice(0, 25).map((r) => r.map((v) => (fin(v) ? FMT.num(v, LOC) : '—')).join('\t'));
        sheet.append(h(t('pdf_table')), pre([d.header[0]].concat(d.live.map((r, i) => 'f' + (i + 1))).join('\t') + '\n' + rows.join('\n')));
    }
    return sheet;
}

function name(ext) {
    const src = (state.rows.find((r) => r.b) || {}).src || 'plot';
    const stem = window.__outputName ? window.__outputName.sanitize(src) : String(src).replace(/[^\w.-]+/g, '-');
    return (stem || 'plot').slice(0, 40) + '.' + ext;
}

function toast(msg, type) { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (_) {} }
function complete(via) { try { if (window.__aeToolComplete) window.__aeToolComplete({ via }); } catch (_) {} }
const ae = (action, p) => { try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, action }, p || {})); } catch (_) {} };

/* Воронка: дошли ли до графика и чем мы не поняли формулу.
   ⚠️ Сама формула не отправляется — только непонятый СИМВОЛ. */
const staged = {};
function stage(nm, meta) {
    if (staged[nm] && nm !== 'error') return;
    staged[nm] = 1;
    try { if (window.__stage) window.__stage(nm, meta || {}); } catch (_) {}
}
/** Что за точка: ноль, вершина, встреча, остановка, пересечение (без чисел). */
function poiWhat(id) {
    const m = /:(r|y|e|i|m|s)\d*/.exec(String(id));
    return m ? ({ r: 'root', y: 'yint', e: 'extremum', i: 'inter', m: 'meet', s: 'stop' })[m[1]] : 'other';
}

/*
 * Какую запись не поняли — КЛАСС, а не сама формула: физика (есть t, голова
 * закона), начальные значения, слова, математика. Отвечает на вопрос «чего
 * мы ещё не читаем», не отправляя то, что человек набрал.
 */
function rowClass(src) {
    const s = String(src || '');
    if (/\p{L}/u.test(s.replace(/[A-Za-zхуасеоркттХУАСЕОРКТπθ]/g, ''))) return 'words';
    if (/[xvhsy]0/i.test(s)) return 'initial';
    if (/^\s*[xyvash][₀-₉0-9]*\s*(\(\s*[tт]\s*\))?\s*=/i.test(s) && /[tт]/.test(s)) return 'motion';
    return /[tт]/.test(s) ? 't_other' : 'math';
}

function badChar(code) {
    const m = /^unexpected_char:(.)/.exec(String(code || ''));
    return m ? m[1] : (String(code || '').split(':')[0] || 'parse');
}

/* ── связка ──────────────────────────────────────────────────────────────── */
function on(id, ev, fn) { const n = $(id); if (n) n.addEventListener(ev, fn); }

function init() {
    if (!$('fpCanvas')) return;

    if (!loadHash()) {
        const saved = window.__toolSettings ? window.__toolSettings.load(SLUG, 'fns', null) : null;
        const list = Array.isArray(saved) && saved.some((s) => String(s || '').trim()) ? saved : [CFG.sample || 'x^2 - 4'];
        list.slice(0, MAX_ROWS).forEach((s) => addRow(s));
    }
    renderInputs();
    setAuto(state.auto);

    on('fpAdd', 'click', () => { addRow(''); renderInputs(); focusRow(state.rows.length - 1); });
    on('fpZoomIn', 'click', () => zoomAt(mid('x'), mid('y'), 1 / 1.3));
    on('fpZoomOut', 'click', () => zoomAt(mid('x'), mid('y'), 1.3));
    on('fpReset', 'click', resetView);
    on('fpAutoY', 'click', () => { setAuto(!state.auto); refresh(false); });
    on('fpPi', 'click', () => {
        state.pi = !piOn();
        $('fpPi').setAttribute('aria-pressed', state.pi ? 'true' : 'false');
        schedule();
        saveHashSoon();
        ae('pi_axis', { on: state.pi ? 1 : 0 });
    });
    on('fpUnits', 'change', (e) => {
        state.units = UNITS[e.target.value] ? e.target.value : 'm-s';
        state.unitsManual = true;
        factsSoon(0);
        schedule();
        saveHashSoon();
        ae('units', { u: state.units, how: 'manual' });
    });
    on('fpPng', 'click', exportPng);
    on('fpCsv', 'click', exportCsv);
    on('fpLink', 'click', copyLink);
    on('fpPdf', 'click', exportPdf);
    on('fpCopyAns', 'click', async () => {
        const ok = await copyText(answersText());
        toast(ok ? t('copied') : t('copy_failed'), ok ? 'success' : 'warning');
        ae('copy_answers', { ok: ok ? 1 : 0 });
    });

    const share = $('fpShare');
    const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    if (share && typeof navigator.share === 'function' && coarse) {
        share.hidden = false;
        share.addEventListener('click', shareLink);
    }
    const cimg = $('fpCopyImg');
    if (cimg && !coarse && window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.write === 'function'
        && typeof window.ClipboardItem === 'function'
        && (typeof ClipboardItem.supports !== 'function' || ClipboardItem.supports('image/png'))) {
        cimg.hidden = false;
        cimg.addEventListener('click', copyImage);
    }

    const stageEl = $('fpStage');
    const full = $('fpFull');
    if (full && stageEl && document.fullscreenEnabled && typeof stageEl.requestFullscreen === 'function') {
        full.hidden = false;
        full.addEventListener('click', () => {
            if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
            else { stageEl.requestFullscreen().catch(() => {}); ae('fullscreen', {}); }
        });
        document.addEventListener('fullscreenchange', () => {
            full.setAttribute('aria-pressed', document.fullscreenElement === stageEl ? 'true' : 'false');
        });
    }

    // Примеры одним нажатием: пустое поле — худший старт для того, кто пришёл
    // посмотреть, как это вообще выглядит.
    document.querySelectorAll('[data-fp-sample]').forEach((b) => {
        b.addEventListener('click', () => {
            state.rows = [];
            state.selected = null;
            state.playing.clear();
            String(b.getAttribute('data-fp-sample')).split('\n').forEach((s) => addRow(s));
            renderInputs();
            state.view = { ...HOME };
            state.viewTouched = false;
            setAuto(true);
            refresh(false);
            ae('sample', { s: b.getAttribute('data-fp-sample') });
        });
    });

    const study = $('fpStudyDrawer');
    if (study) study.addEventListener('toggle', () => { renderStudy(); schedule(); if (study.open) ae('study_open', {}); });
    on('fpStudyRow', 'change', (e) => { state.studyUid = Number(e.target.value); renderStudy(); schedule(); });
    for (const id of ['fpTanX', 'fpIntA', 'fpIntB']) {
        on(id, 'input', () => { renderTangent(); renderIntegral(); schedule(); });
    }
    on('fpTanX', 'change', () => ae('tangent', {}));
    on('fpIntB', 'change', () => ae('integral', {}));
    on('fpDeriv', 'change', (e) => { state.deriv = !!e.target.checked; schedule(); ae('derivative', { on: state.deriv ? 1 : 0 }); });
    const tblDrawer = $('fpTableDrawer');
    if (tblDrawer) tblDrawer.addEventListener('toggle', () => { renderTable(); if (tblDrawer.open) ae('table_open', {}); });
    on('fpStep', 'input', renderTable);

    const hint = $('fpHint');
    if (hint && window.__toolSettings && window.__toolSettings.load(SLUG, 'hinted', 0)) hint.hidden = true;
    // Колеса у пальца нет: на сенсорном экране — подсказка про два пальца.
    if (hint && hint.dataset.touch && window.matchMedia && matchMedia('(pointer: coarse)').matches) hint.textContent = hint.dataset.touch;

    bindCanvas();
    bindKeypad();

    const wrap = $('fpCanvasWrap');
    let lastW = 0, lastH = 0;
    const onSize = () => {
        const { w, h } = canvasSize();
        if (w === lastW && h === lastH) return;
        lastW = w; lastH = h;
        if (state.auto) autoY();
        schedule();
    };
    if (wrap && typeof ResizeObserver === 'function') new ResizeObserver(onSize).observe(wrap);
    else window.addEventListener('resize', onSize);

    // Тема меняется без перезагрузки — холст перечитывает цвета.
    try {
        new MutationObserver(() => schedule()).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    } catch (_) {}
    window.addEventListener('hashchange', () => {
        const cur = '#' + String(location.hash || '').replace(/^#/, '');
        if (loadHash()) { renderInputs(); renderParams(); setAuto(state.auto); refresh(false); void cur; }
    });
    refresh(false);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

// Короткая справка для поддержки: «почему тут такой график» отвечается со
// страницы, без просьбы воспроизвести.
try { window.__fpState = state; } catch (_) {}
